<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from '../../../../components/Modal.vue';
import Button from '../../../../components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const isDropdownOpen = ref(false);
const showModal = ref(false);
const isMobile = ref(window.innerWidth < 768);

const pipelines = computed(() => store.getters['pipelines/getPipelines']);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const uiFlags = computed(() => store.getters['pipelines/getUIFlags']);

const toggleDropdown = () => {
  if (isMobile.value) {
    showModal.value = true;
  } else {
    isDropdownOpen.value = !isDropdownOpen.value;
  }
};

const selectPipeline = async pipeline => {
  try {
    await store.dispatch('pipelines/setSelectedPipeline', pipeline);
    isDropdownOpen.value = false;
    showModal.value = false;
  } catch (error) {
    // Notifica o usuário sobre o erro
    useAlert(t('PIPELINE.ERRORS.PIPELINE_SELECTION_FAILED'));
  }
};

// Atualiza o estado mobile quando a janela é redimensionada
const handleResize = () => {
  isMobile.value = window.innerWidth < 768;
};

const closeDropdown = event => {
  const target = event.target;
  const isDropdownClick = target.closest('.pipeline-selector');

  if (!isDropdownClick) {
    isDropdownOpen.value = false;
  }
};

onMounted(async () => {
  try {
    await store.dispatch('pipelines/fetch');

    // Se não houver funil selecionado e existirem funis, seleciona o primeiro
    if (!selectedPipeline.value && pipelines.value.length > 0) {
      await selectPipeline(pipelines.value[0]);
    }

    window.addEventListener('resize', handleResize);
    document.addEventListener('click', closeDropdown);
  } catch (error) {
    // Notifica o usuário sobre o erro
    useAlert(t('PIPELINE.ERRORS.PIPELINE_FETCH_FAILED'));
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  document.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div class="pipeline-selector relative inline-block">
    <Button
      type="button"
      variant="outline"
      color="slate"
      size="sm"
      :is-loading="uiFlags.isFetching"
      @click="toggleDropdown"
    >
      <template #icon>
        <fluent-icon icon="checkmark-circle" size="16" />
      </template>
      <template v-if="uiFlags.isFetching">
        <span class="md:inline hidden">{{
          $t('PIPELINE.LOADING_PIPELINES')
        }}</span>
      </template>
      <template v-else>
        <span class="md:inline hidden">{{
          selectedPipeline
            ? selectedPipeline.name
            : $t('PIPELINE.SELECT_PIPELINE')
        }}</span>
      </template>
      <fluent-icon
        icon="chevron-down"
        size="16"
        class="md:inline hidden ml-1"
      />
    </Button>

    <!-- Dropdown para desktop -->
    <div
      v-if="isDropdownOpen && !isMobile"
      class="absolute top-[calc(100%+4px)] left-0 z-[9995] min-w-[220px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg p-1.5"
    >
      <div
        v-for="pipeline in pipelines"
        :key="pipeline.id"
        class="px-3 py-2.5 cursor-pointer rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 transition-colors duration-150 hover:bg-slate-100 dark:hover:bg-slate-700"
        :class="
          selectedPipeline?.id === pipeline.id &&
          'bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400'
        "
        @click="selectPipeline(pipeline)"
      >
        {{ pipeline.name }}
      </div>
    </div>

    <!-- Modal para mobile -->
    <Modal
      v-model:show="showModal"
      size="small"
      @close="() => (showModal = false)"
    >
      <div class="p-4">
        <h3 class="text-lg font-medium mb-3 dark:text-slate-100">
          {{ $t('PIPELINE.SELECTOR.TITLE') }}
        </h3>
        <div class="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
          <button
            v-for="pipeline in pipelines"
            :key="pipeline.id"
            class="flex items-center p-3 rounded-lg text-left w-full text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
            :class="
              selectedPipeline?.id === pipeline.id &&
              'bg-woot-50 text-woot-600 dark:bg-woot-900/20 dark:text-woot-300'
            "
            @click="selectPipeline(pipeline)"
          >
            <fluent-icon icon="checkmark-circle" size="18" class="mr-2" />
            <div class="font-medium break-words">
              {{ pipeline.name }}
            </div>
            <fluent-icon
              v-if="selectedPipeline?.id === pipeline.id"
              icon="checkmark"
              size="16"
              class="ml-auto text-woot-500"
            />
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>
